import { useState, useEffect, useCallback, useRef } from 'react';
import { Redis } from "@upstash/redis";
import { useUser } from "@/lib/context/user-context";
import { supabase } from '@/core/supabase';

// Global cache to prevent duplicate fetches across component re-renders
const globalFetchCache = new Map<string, Promise<any>>();
const globalDataCache = new Map<string, any>();

// 添加全局缓存清理机制
const MAX_GLOBAL_CACHE_SIZE = 3; // 最多缓存5个episode
function cleanupGlobalCache() {
  if (globalDataCache.size > MAX_GLOBAL_CACHE_SIZE) {
    const entries = Array.from(globalDataCache.entries());
    const toDelete = entries.slice(0, entries.length - MAX_GLOBAL_CACHE_SIZE);
    toDelete.forEach(([key]) => {
      globalDataCache.delete(key);
      globalFetchCache.delete(key);
    });
    console.log(`🧹 Cleaned ${toDelete.length} old episodes from global cache`);
  }
}

const redis = new Redis({
  url: process.env.PLASMO_PUBLIC_UPSTASH_REDIS_URL || "https://handy-boar-62852.upstash.io",
  token: process.env.PLASMO_PUBLIC_UPSTASH_REDIS_TOKEN || "AfWEAAIjcDEzOTQxYmI1NzA5NjU0YTc0OThmM2ViZGJlZTg3Yjc3YnAxMA",
});

interface ShareData {
  userId: string;
  shareId: string;
  origin?: string;
  videoId: string;
  videoUrl: string;
  audioUrl?: string; // 🔥 添加audioUrl字段，用于blob URL
  title: string;
  titleTranslation?: string;
  author: string;
  thumbnail: string;
  visibility: string;
  createdAt: string;
  ownerAvatarUrl: string;
  publishDate: string;
  description: string;
  keywords: string;
  hydrationPending?: boolean; // 🔥 标记需要 hydration
  content?: {
    summary: string | null;
    chats: any[];
    transcript: any[];
    vocabulary?: {
      enabled: boolean;
      language: string;
    };
    mindmap?: string | null;
    cleanRead?: any | null;
    learningSidebar?: any | null;
    hydrationPending?: boolean; // 🔥 内容层 hydration 标志
  };
}

// 缓存已转换的数据，避免重复处理
const dataCache = new Map<string, any>();
const MAX_CACHE_SIZE = 10; // 限制缓存大小防止内存泄漏

// 清理缓存
function cleanupDataCache() {
  if (dataCache.size > MAX_CACHE_SIZE) {
    // 保留最近的缓存项
    const entries = Array.from(dataCache.entries());
    const toKeep = entries.slice(-MAX_CACHE_SIZE);
    dataCache.clear();
    toKeep.forEach(([key, value]) => dataCache.set(key, value));
  }
}

// 检测数据格式，避免不必要的转换
function detectDataFormat(data: any[]): "chunks" | "sentences" | "unknown" {
  if (!data || data.length === 0) return "unknown";
  const firstSegment = data[0];
  if (firstSegment?.chunks) return "chunks";
  if (firstSegment?.sentences) return "sentences";
  return "unknown";
}

// 优化版本：只在必要时转换，并使用缓存
function optimizedDataProcessor(inputData: any, cacheKey?: string) {
  if (!inputData || !Array.isArray(inputData)) {
    return [];
  }

  if (cacheKey && dataCache.has(cacheKey)) {
    return dataCache.get(cacheKey);
  }

  const format = detectDataFormat(inputData);
  let result;

  switch (format) {
    case "chunks":
      result = inputData.map((segment) => ({
        ...segment,
        timestamp: segment.timestamp || [0, 0],
        translation: segment.translation,
        chunks: segment.chunks?.map((chunk: any) => ({
          ...chunk,
          timestamp: chunk.timestamp || [0, 0],
          translation: chunk.translation,
        })) || [],
      }));
      break;

    case "sentences":
      result = inputData.map((segment: any) => {
        const sentences = segment.sentences?.map((sentence: any) => ({
          text: sentence.text || "",
          translation: sentence.translation,
          timestamp: [
            (sentence.startTime || 0) / 1000,
            (sentence.endTime || 0) / 1000,
          ],
        })) || [];

        return {
          text: segment.text || "",
          translation: segment.translation,
          timestamp: [
            (segment.startTime || 0) / 1000,
            (segment.endTime || 0) / 1000,
          ],
          sentences: sentences,
          chunks: sentences,
        };
      });
      break;

    default:
      result = inputData.map((segment: any) => {
        const singleChunk = {
          text: segment.text || "",
          translation: segment.translation,
          timestamp: [
            (segment.startTime || 0) / 1000,
            (segment.endTime || 0) / 1000,
          ],
        };

        return {
          text: segment.text || "",
          translation: segment.translation,
          timestamp: singleChunk.timestamp,
          sentences: [singleChunk],
          chunks: [singleChunk],
        };
      });
  }

  if (cacheKey && result) {
    dataCache.set(cacheKey, result);
    cleanupDataCache(); // 防止缓存无限增长
  }

  return result;
}

async function fetchBasicYouTubeMetadata(videoId: string) {
  try {
    const response = await fetch(
      `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return {
      title: data?.title ?? null,
      author: data?.author_name ?? null,
      thumbnail: data?.thumbnail_url ?? null,
      description: data?.description ?? null,
    };
  } catch (error) {
    console.warn('Failed to fetch basic YouTube metadata:', error);
    return null;
  }
}

export function useEpisodeData(episodeId: string) {
  const [content, setContent] = useState<any>(null);
  const [groupedSentences, setGroupedSentences] = useState<any[]>([]);
  const [summary, setSummary] = useState<string>("");
  const [mindmapData, setMindmapData] = useState("");
  const [vocabulary, setVocabulary] = useState<any | null>(null);
  const [cleanReadData, setCleanReadData] = useState<any | null>(null);
  const [learningSidebarData, setLearningSidebarData] = useState<any | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState({
    data: true, // Start with true to show loading on initial load
  });
  // Add access info state
  const [accessInfo, setAccessInfo] = useState<any>(null);
  // Add generation records state
  const [generationRecords, setGenerationRecords] = useState<any>(null);
  // 🔥 Add allowBasicPlanLite state
  const [allowBasicPlanLite, setAllowBasicPlanLite] = useState<boolean>(false);
  // Add fetch tracking to prevent duplicate calls
  const [lastFetchedId, setLastFetchedId] = useState<string>('');
  const fetchInProgressRef = useRef<boolean>(false);
  
  const { user, session, isLoading: userLoading } = useUser();

  // 🔍 Debug effect to track generationRecords state changes
  useEffect(() => {
    console.log('🔄 generationRecords state changed in useEpisodeData:', generationRecords);
  }, [generationRecords]);

  const loadContent = useCallback(async () => {
    // Wait for user state to load before making API calls
    if (userLoading) {
      return;
    }
    
    // Check if fetch is already in progress or data exists in global cache
    if (fetchInProgressRef.current) {
      return;
    }
    
    // Memory profiling in development
    if (process.env.NODE_ENV === 'development') {
      const profiler = (window as any).memoryProfiler;
      if (profiler) {
        profiler.mark(`episode-${episodeId}-fetch-start`);
      }
    }
    
    if (globalDataCache.has(episodeId)) {
      const cachedData = globalDataCache.get(episodeId);

      // Use cached content data but always refresh access info based on current user state
      setContent(cachedData.content);
      setGroupedSentences(cachedData.groupedSentences);
      setSummary(cachedData.summary || '');
      setMindmapData(cachedData.mindmapData || '');
      setVocabulary(cachedData.vocabulary || null);
      setCleanReadData(cachedData.cleanReadData || null);
      setLearningSidebarData(cachedData.learningSidebarData || null);

      // Important: Set loading to false when using cached data
      setLoadingProgress((p) => ({ ...p, data: false }));
      
      // Don't use cached accessInfo - always fetch fresh access info
      // Continue to API call to get current access status
    }
    
    // Check if there's already a fetch promise for this episode
    if (globalFetchCache.has(episodeId)) {
      const existingPromise = globalFetchCache.get(episodeId);
      const result = await existingPromise;
      if (result) {
        setContent(result.content);
        setAccessInfo(result.accessInfo);
        setGroupedSentences(result.groupedSentences);
        setSummary(result.summary || '');
        setMindmapData(result.mindmapData || '');
        setVocabulary(result.vocabulary || null);
        setCleanReadData(result.cleanReadData || null);
        setLearningSidebarData(result.learningSidebarData || null);
      }
      return;
    }
    
    try {
      fetchInProgressRef.current = true;
      setLoadingProgress((p) => ({ ...p, data: true }));

      // 确保获取最新的session
      let currentSession = session;
      
      // 如果有user但没有session，尝试重新获取session
      if (user && !session?.access_token) {
        const { data: { session: refreshedSession } } = await supabase.auth.getSession();
        if (refreshedSession) {
          currentSession = refreshedSession;
        }
      }
      
      // 首先尝试使用浏览器缓存的API获取数据
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // 优先使用刷新后的session token
      const token = currentSession?.access_token || localStorage.getItem('token');
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Allow normal HTTP caching (ETag/304) to avoid slow re-fetches on refresh
      const cachedResponse = await fetch(`/api/episodes/${episodeId}`, {
        method: 'GET',
        headers,
        // Let the browser manage cache; do not force no-store here
      }).catch(() => null);

      let shareData: ShareData | null = null;
      let currentAccessInfo: any = null; // Declare here to be available throughout the function

      if (cachedResponse && cachedResponse.ok) {
        const responseData = await cachedResponse.json();
        shareData = responseData.content;
        // Store access info from API response
        currentAccessInfo = {
          hasAccess: responseData.hasAccess,
          isFeaturedEpisode: responseData.isFeaturedEpisode,
          accessLevel: responseData.accessLevel,
          reason: responseData.accessReason
        };
        // 🔥 设置生成记录，添加 _verifiedForUser 标记
        // 这样 GlobalPlayerControls 不会认为需要重新 hydration
        console.log('🔍 API response generationRecords:', responseData.generationRecords);
        if (responseData.generationRecords) {
          const verifiedGenerationRecords = {
            ...responseData.generationRecords,
            _verifiedForUser: user?.id || null,
          };
          console.log('⏰ BEFORE setGenerationRecords - current state:', generationRecords);
          setGenerationRecords(verifiedGenerationRecords);
          console.log('✅ AFTER setGenerationRecords call - target:', verifiedGenerationRecords);
          
          // 延迟检查状态是否真的更新了
          setTimeout(() => {
            console.log('🔄 Delayed check - generationRecords should be:', verifiedGenerationRecords);
          }, 100);
        } else {
          console.log('⚠️ No generationRecords in API response');
        }
        // 🔥 设置 allowBasicPlanLite 字段
        if (responseData.allowBasicPlanLite !== undefined) {
          setAllowBasicPlanLite(responseData.allowBasicPlanLite === true);
        }
        // If user is authenticated but access is denied, likely a token issue
        if (user && !responseData.hasAccess && responseData.accessLevel === 'guest') {
          // Try to refresh the session
          const { data: { session: newSession } } = await supabase.auth.refreshSession();
          
          if (newSession?.access_token) {
            // Retry with refreshed token
            const retryResponse = await fetch(`/api/episodes/${episodeId}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${newSession.access_token}`
              },
              cache: 'no-store' // Force fresh request
            });
            
            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              shareData = retryData.content;
              currentAccessInfo = {
                hasAccess: retryData.hasAccess,
                isFeaturedEpisode: retryData.isFeaturedEpisode,
                accessLevel: retryData.accessLevel,
                reason: retryData.accessReason
              };
              // 🔥 设置生成记录，添加 _verifiedForUser 标记
              console.log('🔍 Retry API response generationRecords:', retryData.generationRecords);
              if (retryData.generationRecords) {
                const verifiedGenerationRecords = {
                  ...retryData.generationRecords,
                  _verifiedForUser: user?.id || null,
                };
                console.log('⏰ RETRY - BEFORE setGenerationRecords - current state:', generationRecords);
                setGenerationRecords(verifiedGenerationRecords);
                console.log('✅ RETRY - AFTER setGenerationRecords call - target:', verifiedGenerationRecords);
              } else {
                console.log('⚠️ No generationRecords in retry response');
              }
              // 🔥 设置 allowBasicPlanLite 字段
              if (retryData.allowBasicPlanLite !== undefined) {
                setAllowBasicPlanLite(retryData.allowBasicPlanLite === true);
              }
            }
          }
        }
        
        // 🚀 性能优化：将 YouTube 头像获取移到后台异步执行，避免阻塞主要内容加载
        if (shareData && shareData.origin === 'video' && !shareData.ownerAvatarUrl && shareData.videoId) {
          // 先设置内容，不等待头像
          const videoIdForAvatar = shareData.videoId;
          setTimeout(async () => {
            try {
              const youtubeResponse = await fetch(`https://yt-api.p.rapidapi.com/video/info?id=${videoIdForAvatar}`, {
              method: 'GET',
              headers: {
                'x-rapidapi-host': 'yt-api.p.rapidapi.com',
                'x-rapidapi-key': 'fc554a1fd9msh36570017644599cp135d7ajsn17e548380b61',
              },
            });
            
            if (youtubeResponse.ok) {
              const youtubeData = await youtubeResponse.json();
              if (youtubeData) {
                // 尝试多种获取头像的方法
                let ownerAvatarUrl = '';
                
                // 方法1: channelThumbnail 数组
                if (youtubeData.channelThumbnail && Array.isArray(youtubeData.channelThumbnail) && youtubeData.channelThumbnail.length > 0) {
                  ownerAvatarUrl = youtubeData.channelThumbnail[0]?.url || '';
                }
                
                // 方法2: channelThumbnail 直接对象
                if (!ownerAvatarUrl && youtubeData.channelThumbnail?.url) {
                  ownerAvatarUrl = youtubeData.channelThumbnail.url;
                }
                
                // 方法3: avatar 字段
                if (!ownerAvatarUrl && youtubeData.avatar) {
                  ownerAvatarUrl = Array.isArray(youtubeData.avatar) ? youtubeData.avatar[0]?.url : youtubeData.avatar.url || youtubeData.avatar;
                }
                
                // 方法4: 从频道信息中获取
                if (!ownerAvatarUrl && youtubeData.channel?.avatar) {
                  ownerAvatarUrl = Array.isArray(youtubeData.channel.avatar) ? youtubeData.channel.avatar[0]?.url : youtubeData.channel.avatar.url || youtubeData.channel.avatar;
                }
                
                if (ownerAvatarUrl && shareData) {
                  shareData.ownerAvatarUrl = ownerAvatarUrl;
                } else if (youtubeData.channelId) {
                  // 如果没有直接的头像，使用频道ID获取频道信息
                  try {
                    const channelResponse = await fetch(`https://yt-api.p.rapidapi.com/channel/about?id=${youtubeData.channelId}`, {
                      method: 'GET',
                      headers: {
                        'x-rapidapi-host': 'yt-api.p.rapidapi.com',
                        'x-rapidapi-key': 'fc554a1fd9msh36570017644599cp135d7ajsn17e548380b61',
                      },
                    });
                    
                    if (channelResponse.ok) {
                      const channelData = await channelResponse.json();
                      
                      let channelAvatar = '';
                      
                      // 尝试从频道数据中获取头像
                      if (channelData.avatar && Array.isArray(channelData.avatar)) {
                        channelAvatar = channelData.avatar[0]?.url || '';
                      } else if (channelData.avatar?.url) {
                        channelAvatar = channelData.avatar.url;
                      } else if (channelData.avatar) {
                        channelAvatar = channelData.avatar;
                      }
                      
                      // 尝试其他可能的字段
                      if (!channelAvatar && channelData.thumbnail) {
                        if (Array.isArray(channelData.thumbnail)) {
                          channelAvatar = channelData.thumbnail[0]?.url || '';
                        } else if (channelData.thumbnail.url) {
                          channelAvatar = channelData.thumbnail.url;
                        }
                      }
                      
                      if (!channelAvatar && channelData.channelThumbnail) {
                        if (Array.isArray(channelData.channelThumbnail)) {
                          channelAvatar = channelData.channelThumbnail[0]?.url || '';
                        } else if (channelData.channelThumbnail.url) {
                          channelAvatar = channelData.channelThumbnail.url;
                        }
                      }
                      
                      if (channelAvatar) {
                        // 头像获取成功后，更新 content 状态触发重新渲染
                        setContent((prevContent: any) => {
                          if (!channelAvatar || !prevContent) {
                            return prevContent;
                          }

                          const previousAvatar =
                            prevContent?.ownerAvatarUrl ??
                            prevContent?.content?.ownerAvatarUrl ??
                            null;

                          if (previousAvatar === channelAvatar) {
                            return prevContent;
                          }

                          return {
                            ...prevContent,
                            ownerAvatarUrl: channelAvatar,
                            content: prevContent?.content
                              ? {
                                  ...prevContent.content,
                                  ownerAvatarUrl: channelAvatar,
                                }
                              : prevContent?.content,
                          };
                        });
                      }
                    }
                  } catch (channelError: any) {
                    console.error('Failed to fetch channel info', channelError);
                    // 静默处理频道信息获取失败
                  }
                } else if (ownerAvatarUrl) {
                  // 如果直接获取到头像，更新状态
                  setContent((prevContent: any) => {
                    if (!ownerAvatarUrl || !prevContent) {
                      return prevContent;
                    }

                    const previousAvatar =
                      prevContent?.ownerAvatarUrl ??
                      prevContent?.content?.ownerAvatarUrl ??
                      null;

                    if (previousAvatar === ownerAvatarUrl) {
                      return prevContent;
                    }

                    return {
                      ...prevContent,
                      ownerAvatarUrl: ownerAvatarUrl,
                      content: prevContent?.content
                        ? {
                            ...prevContent.content,
                            ownerAvatarUrl: ownerAvatarUrl,
                          }
                        : prevContent?.content,
                    };
                  });
                }
              }
            }
            } catch (error: any) {
              console.error('Failed to fetch YouTube info', error);
              // 静默处理YouTube头像获取失败
            }
          }, 0); // 推到下一个事件循环执行
        }
        
        setAccessInfo(currentAccessInfo);

        // 🚀 性能优化：立即设置主要内容并结束 loading，不等待 YouTube 头像
        // processSharedContent 将在下面统一处理
      } else {
        // 如果API调用失败，尝试回退到直接Redis查询
        try {
          const contentKey = `share:${episodeId}`;
          shareData = (await redis.get(contentKey)) as ShareData;
        } catch (redisError) {
          console.warn('Redis fallback query failed:', redisError);
          // Redis 查询失败，shareData 保持为 null，后续会创建默认数据
        }
      }

      if (!shareData) {
        // 🔥 如果Redis中没有数据，立即创建默认空数据结构并结束 loading
        // YouTube 元数据获取移到后台异步执行，避免阻塞
        console.warn("Share data not found for ID:", episodeId, "- using default empty structure");
        
        // 先使用基本信息创建 shareData，让页面快速显示 "Generate Transcript" 按钮
        // 🔥 注意：不设置 hydrationPending，因为这已经是最终状态
        // GlobalPlayerControls 在 hydration 失败时也会清除 hydrationPending 标志
        shareData = {
          userId: 'unknown',
          shareId: episodeId,
          origin: 'video',
          videoId: episodeId,
          videoUrl: `https://www.youtube.com/watch?v=${episodeId}`,
          title: `YouTube Video`,
          titleTranslation: undefined,
          author: 'YouTube',
          thumbnail: `https://i.ytimg.com/vi/${episodeId}/hqdefault.jpg`,
          visibility: 'public',
          createdAt: new Date().toISOString(),
          ownerAvatarUrl: `https://i.ytimg.com/vi/${episodeId}/hqdefault.jpg`,
          publishDate: new Date().toISOString(),
          description: '',
          keywords: '',
          content: {
            summary: null,
            chats: [],
            transcript: [],
            vocabulary: undefined,
            mindmap: null,
            cleanRead: null,
            learningSidebar: null,
          }
        };
        
        // 🔥 为默认数据设置 accessInfo，避免 ContentRenderer 一直显示 loading
        currentAccessInfo = {
          hasAccess: true, // YouTube 视频默认允许访问
          isFeaturedEpisode: false,
          accessLevel: 'public',
          reason: 'New YouTube video'
        };
        setAccessInfo(currentAccessInfo);
        
        // 🚀 后台异步获取 YouTube 元数据，不阻塞主流程
        const videoIdForMeta = episodeId;
        setTimeout(async () => {
          try {
            const basicMeta = await fetchBasicYouTubeMetadata(videoIdForMeta);
            if (basicMeta) {
              setContent((prevContent: any) => {
                if (!prevContent) return prevContent;
                return {
                  ...prevContent,
                  title: basicMeta.title || prevContent.title,
                  author: basicMeta.author || prevContent.author,
                  thumbnail: basicMeta.thumbnail || prevContent.thumbnail,
                  description: basicMeta.description || prevContent.description,
                  content: prevContent.content ? {
                    ...prevContent.content,
                    title: basicMeta.title || prevContent.content?.title,
                    author: basicMeta.author || prevContent.content?.author,
                    thumbnail: basicMeta.thumbnail || prevContent.content?.thumbnail,
                  } : prevContent.content
                };
              });
            }
          } catch (error) {
            console.warn('Failed to fetch YouTube video info in background:', error);
          }
        }, 0);
      }

      if (
        shareData &&
        shareData.origin === 'video' &&
        shareData.videoId &&
        typeof shareData.title === 'string' &&
        shareData.title.trim().toLowerCase().includes('loading video')
      ) {
        const fallbackMeta = await fetchBasicYouTubeMetadata(shareData.videoId);
        if (fallbackMeta) {
          shareData = {
            ...shareData,
            title: fallbackMeta.title || shareData.title,
            author: fallbackMeta.author || shareData.author,
            thumbnail: fallbackMeta.thumbnail || shareData.thumbnail,
            description: fallbackMeta.description || shareData.description,
            ownerAvatarUrl:
              shareData.ownerAvatarUrl ||
              fallbackMeta.thumbnail ||
              shareData.thumbnail,
          };
        }
      }

      if (shareData.visibility === "private") {
        // Skip validation if user state is temporarily unavailable (e.g., during tab switch)
        if (user === null) {
          console.warn("User state temporarily unavailable, skipping validation");
          return;
        }
        
        if (!user?.id) {
          console.warn("未登录用户访问私有内容");
          setError(true);
          return;
        }

        if (user.id !== shareData.userId) {
          console.warn("用户无权限访问此内容");
          setError(true);
          return;
        }
      }

      const legacyContent = (shareData.content as any)?.content || {};
      const deepLegacyContent = legacyContent?.content || {};

      const normalizeTranscriptCandidate = (candidate: any): any[] => {
        if (!candidate) return [];

        let value = candidate;
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (error) {
            console.warn('Failed to parse transcript string for episode', episodeId, error);
            return [];
          }
        }

        if (Array.isArray(value)) {
          return value;
        }

        if (typeof value === 'object') {
          if (Array.isArray((value as any).transcript)) {
            return (value as any).transcript;
          }
          if (Array.isArray((value as any).segments)) {
            return (value as any).segments;
          }
          if (Array.isArray((value as any).sentences)) {
            return (value as any).sentences;
          }

          const objectValues = Object.values(value);
          if (objectValues.length > 0 && objectValues.every(item => typeof item === 'object')) {
            return objectValues;
          }
        }

        return [];
      };

      const transcriptCandidates = [
        shareData.content?.transcript,
        legacyContent?.transcript,
        deepLegacyContent?.transcript,
      ];

      let rawTranscript: any[] = [];
      for (const candidate of transcriptCandidates) {
        const normalized = normalizeTranscriptCandidate(candidate);
        if (normalized.length > 0) {
          rawTranscript = normalized;
          break;
        }
      }

      if (rawTranscript.length === 0 && shareData.content?.transcript) {
        rawTranscript = normalizeTranscriptCandidate(shareData.content?.transcript);
      }
      const convertedData = optimizedDataProcessor(
        rawTranscript,
        `transcript:${episodeId}:v3`
      );

      if (convertedData.length > 0 && !convertedData[0].translation && rawTranscript.length > 0 && rawTranscript[0].translation) {
          console.warn('[useEpisodeData] Warning: Translation lost during processing!', {
              raw: rawTranscript[0].translation,
              processed: convertedData[0].translation
          });
      }

      const summaryData =
        shareData.content?.summary ??
        legacyContent?.summary ??
        deepLegacyContent?.summary ??
        null;
      const mindmapDataValue =
        shareData.content?.mindmap ??
        legacyContent?.mindmap ??
        deepLegacyContent?.mindmap ??
        null;
      const vocabularyData =
        shareData.content?.vocabulary ??
        legacyContent?.vocabulary ??
        deepLegacyContent?.vocabulary ??
        null;
      const cleanReadDataValue =
        shareData.content?.cleanRead ??
        legacyContent?.cleanRead ??
        deepLegacyContent?.cleanRead ??
        null;
      const learningSidebarValue =
        shareData.content?.learningSidebar ??
        legacyContent?.learningSidebar ??
        deepLegacyContent?.learningSidebar ??
        null;

      const normalizedData = {
        content: {
          userId: shareData.userId,
          shareId: shareData.shareId,
          origin: shareData.origin,
          videoId: shareData.videoId,
          videoUrl: shareData.videoUrl,
          audioUrl: shareData.audioUrl, // 🔥 添加audioUrl字段，确保blob URL传递到播放器
          title: shareData.title,
          titleTranslation: shareData.titleTranslation,
          author: shareData.author,
          thumbnail: shareData.thumbnail,
          visibility: shareData.visibility,
          createdAt: shareData.createdAt,
          ownerAvatarUrl: shareData.ownerAvatarUrl,
          publishDate: shareData.publishDate,
          description: shareData.description,
          keywords: shareData.keywords,
          // 🔥 传递 hydrationPending 标志
          hydrationPending: shareData.hydrationPending,
          content: {
            summary: summaryData,
            chats: shareData.content?.chats || legacyContent?.chats || deepLegacyContent?.chats || [],
            transcript: convertedData,
            vocabulary: vocabularyData,
            mindmap: mindmapDataValue,
            cleanRead: cleanReadDataValue,
            // 🔥 传递内层 hydrationPending 标志
            hydrationPending: shareData.content?.hydrationPending || shareData.hydrationPending,
          },
          learningSidebar: learningSidebarValue,
        },
      };

      setGroupedSentences(normalizedData.content.content.transcript);
      setContent(normalizedData);

      if (summaryData) {
        setSummary(summaryData);
      }

      if (mindmapDataValue) {
        setMindmapData(mindmapDataValue);
      }

      if (vocabularyData) {
        setVocabulary(vocabularyData);
      }

      if (cleanReadDataValue) {
        setCleanReadData(cleanReadDataValue);
      }

      if (learningSidebarValue) {
        setLearningSidebarData(learningSidebarValue);
      }
      
      // Cache the successful result - use the local currentAccessInfo instead of state
      const cacheData = {
        content: normalizedData,
        accessInfo: currentAccessInfo, // Use the local variable instead of state
        groupedSentences: normalizedData.content.content.transcript,
        summary: summaryData || '',
        mindmapData: mindmapDataValue || '',
        vocabulary: vocabularyData || null,
        cleanReadData: cleanReadDataValue || null,
        learningSidebarData: learningSidebarValue || null
      };
      globalDataCache.set(episodeId, cacheData);
      cleanupGlobalCache(); // 清理旧的episode缓存

      // Memory profiling in development
      if (process.env.NODE_ENV === 'development') {
        const profiler = (window as any).memoryProfiler;
        if (profiler) {
          profiler.mark(`episode-${episodeId}-fetch-complete`);
          profiler.measure(`episode-${episodeId}-fetch-start`, `episode-${episodeId}-fetch-complete`);
        }
      }

    } catch (error: any) {
      console.error('Failed to fetch episode data', error);
      setError(true);
      globalFetchCache.delete(episodeId); // Remove failed fetch from cache
    } finally {
      setLoadingProgress((p) => ({ ...p, data: false }));
      fetchInProgressRef.current = false;
      globalFetchCache.delete(episodeId); // Clean up fetch promise
    }
  }, [episodeId, user, session, userLoading]);

  useEffect(() => {
    // Only reload if we don't have content data or episode changed, and user state is loaded
    if (episodeId && episodeId !== lastFetchedId && (!content || !content.content) && !fetchInProgressRef.current && !userLoading) {
      setLastFetchedId(episodeId);
      loadContent();
    } else if (!episodeId) {
      // If no episodeId, set loading to false
      setLoadingProgress((p) => ({ ...p, data: false }));
    }
  }, [episodeId, lastFetchedId, loadContent]); // Add loadContent back to ensure latest version is used

  // 清理缓存的工具函数
  const clearDataCache = useCallback(() => {
    dataCache.clear();
  }, []);

  // 清理全局缓存的工具函数
  const clearGlobalCache = useCallback(() => {
    globalDataCache.delete(episodeId);
    globalFetchCache.delete(episodeId);
  }, [episodeId]);

  return {
    content,
    setContent,
    groupedSentences,
    setGroupedSentences,
    summary,
    setSummary,
    mindmapData,
    setMindmapData,
    vocabulary,
    setVocabulary,
    cleanReadData,
    setCleanReadData,
    learningSidebarData,
    setLearningSidebarData,
    error,
    loadingProgress,
    clearDataCache,
    clearGlobalCache,
    loadContent,
    // Add access info
    accessInfo,
    // Add generation records
    generationRecords,
    setGenerationRecords,
    // 🔥 Add allowBasicPlanLite
    allowBasicPlanLite,
  };
} 
